import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { refreshAuthTokenAndUpdateUser } from '@/auth/utils'

const API_URL = import.meta.env.VITE_API_URL

type AddRequestPayload = {
    course_code: string
    section: string
    semester: string
    requested_by: string
}

type UpdateStatusPayload = {
    request_id: string
    status: 'Approved' | 'Rejected'
    reviewer_id: string
}

export default function useRequestMutation() {
    const queryClient = useQueryClient()

    const addRequestMutation = useMutation({
        mutationFn: async (newRequest: AddRequestPayload) => {
            const idToken = await refreshAuthTokenAndUpdateUser()
            const response = await fetch(`${API_URL}/api/requests/add`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${idToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(newRequest),
            })

            if (!response.ok) {
                const body = await response.json()
                throw new Error(body.message || 'Failed to submit request')
            }
            return response.json()
        },
        onSuccess: () => {
            toast.success('Course request submitted successfully')
            queryClient.invalidateQueries({ queryKey: ['requests'] })
            queryClient.invalidateQueries({ queryKey: ['requests', 'my'] })
        },
        onError: (error: Error) => {
            toast.error(error.message)
        },
    })

    const updateStatusMutation = useMutation({
        mutationFn: async (payload: UpdateStatusPayload) => {
            const idToken = await refreshAuthTokenAndUpdateUser()
            const response = await fetch(`${API_URL}/api/requests/update-status`, {
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${idToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            })

            if (!response.ok) {
                const body = await response.json()
                throw new Error(body.message || 'Failed to update request')
            }
            return response.json()
        },
        onSuccess: (_, variables) => {
            toast.success(`Request ${variables.status.toLowerCase()} successfully`)
            queryClient.invalidateQueries({ queryKey: ['requests'] })
            queryClient.invalidateQueries({ queryKey: ['requests', 'my'] })
            if (variables.status === 'Approved') {
                queryClient.invalidateQueries({ queryKey: ['courses'] })
            }
        },
        onError: (error: Error) => {
            toast.error(error.message)
        },
    })

    return { addRequest: addRequestMutation, updateStatus: updateStatusMutation }
}
